import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import Factory from '@/Factories/Factory';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import Label from '@/Controls/Label';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import PropertyGroupFactory from '@/Factories/GroupFactories/PropertyGroupFactory';
import * as CoreFrameDTOs from "coreframetypings/dto";
import { IDetailViewDTO, IDetailViewGroupDTO, IDetailViewContextDTO } from "coreframetypings/dto";
import GenericObject from '@/Model/GenericObject';
import { NestedContextItemChangedDTO, INestedDetailViewContextConstructorParameter } from "#/DefaultViewExtensionInterfaces";
import RADEnvironmentProxy from "@/Communication/RADEnvironmentProxy";
import UIListener from "@/UIListener/UIListener";
import Utilities from "@/Utility/Utilities";

interface INestedDetailViewRefreshData {
    parentContextID: string;
    nestedContextName: string;
    typeName: string;
    pdoId: string;
}

export default class NestedDetailViewContextFactory extends ControlFactory {
    private jQueryControl: JQuery;
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
    protected Label = ComponentManager.getControl<typeof Label>('Label');
    
    protected parentContext: DetailViewContext;
    public nestedContextName: string;
    public nestedContext: KnockoutObservable<DetailViewContext>;
    public groupFactory: PropertyGroupFactory;

    /**
     * If set and found, only the items of the isolated group will be shown, without any surrounding groups.
     */
    private readonly isolatedGroup: string;

    /**
     * @override
     */
    public viewModel: LabelViewModel;

    public constructor(params: INestedDetailViewContextConstructorParameter) {
        super(params);

        this.parentContext = this.context;
        this.nestedContextName = this.detailViewItem.name;
        this.nestedContext = this.observableOrDefault(params.nestedContext, this.context.viewModel.createNestedContext(this.nestedContextName, this.parentContext));
        
        this.labelPosition = this.paramOrDefault('None');
        this.colspan = this.paramOrDefault('Fill');
        this.isolatedGroup = this.paramOrDefault(params.isolatedGroup, null);
    }

    /**
     * @override
     */
    control: Label;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = $('<div>');

        if (this.nestedContext != null) {
            let currentSubscription: KnockoutSubscription = null;
            this.defineSubscription(this.nestedContext, nestedContext => {
                if (this.nestedContext() == null)
                    return;
                
                currentSubscription?.dispose();
                const callback = (detailView:IDetailViewDTO)=> {
                    if (detailView == null)
                        return;

                    this.groupFactory = this.createPropertyGroupFactory(detailView);

                    this.groupFactory.getTupleAsync().then(tuple => {
                        this.jQueryControl.empty().append(tuple.control);
                    });
                };

                currentSubscription = nestedContext.detailView.subscribe(callback);
                this.subscriptionCollection.push(currentSubscription);
                callback(nestedContext.detailView());
            })(this.nestedContext());
        } else {
            this.viewModel = this.createViewModel();

            this.control = new this.Label(this.viewModel);

            let tuple = this.control.getTuple();
            tuple.control.appendTo(this.jQueryControl);
        }

        return this.jQueryControl;
    };

    private createPropertyGroupFactory(detailView: IDetailViewDTO): PropertyGroupFactory {
        const group: IDetailViewGroupDTO = {
            ...detailView,
            items: [],
            uiGenerator: undefined,
        };

        let layout = 'NoneLayout';

        const isolatedGroup = this.getIsolatedGroup(detailView);
        if (isolatedGroup != null) {
            group.detailViewGroups = [];
            group.items = isolatedGroup.items;

            // Notwendig, damit die DV als "ready" gelten kann (alle Groups und Items gerendert)
            const dv = this.nestedContext()?.detailViewContextDTO.detailView;
            if (Utilities.isNullOrUndefined(dv))
                return null;
            
            dv.detailViewGroups = group.detailViewGroups;
            (dv as any).items = group.items;
            this.nestedContext().collectAllItems();

            const uiGenerator = Factory.parseUIGeneratorQuery(isolatedGroup.uiGenerator);
            layout = this.nestedContext.isSummaryViewMode()
                ? 'OneColumnLayout'
                : uiGenerator.parameters['layout'];
        }

        return Factory.createInstance<PropertyGroupFactory>('PropertyGroup', {
            detailViewGroup: group,
            context: this.nestedContext(),
            layout: layout,
            locator: this.nestedContext().locator
        });
    }

    private getIsolatedGroup(detailView: IDetailViewDTO): IDetailViewGroupDTO {
        if (this.isolatedGroup == null)
            return null;

        return detailView.detailViewGroups.filter(g => g.name === this.isolatedGroup)[0];
    }

    private createViewModel(): LabelViewModel {
        console.log('Die NestedDetailViewContextFactory für das Element "' + this.detailViewItem.name + '" konnte nicht erzeugt werden weil das dazugehörige DTO-Objekt nicht gefunden wurde.');

        return new this.LabelViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            isErrorLabel: true,
            ergName: 'DELETED_ERROR'
        });
    }

    public async refreshNestedDetailViewContext(inputChangedDto: NestedContextItemChangedDTO): Promise<IDetailViewContextDTO> {
        if (!this.nestedContextName) {
            console.error('Could not determine any NestedDetailViewContext item');
            return Promise.resolve(null);
        }

        const refreshData: INestedDetailViewRefreshData = {
            parentContextID: this.parentContext.id,
            nestedContextName: this.nestedContextName,
            typeName: inputChangedDto.TypeName,
            pdoId: inputChangedDto.PDO_ID
        }

        return RADEnvironmentProxy.getInstance().refreshNestedDetailViewContext(refreshData).then((data: IDetailViewContextDTO) => {
            const nestedContext = this.createNewNestedContext(data, this.nestedContextName, this.parentContext);
            this.reloadNestedContext(this.parentContext, nestedContext, data);

            return data;
        });
    }

    private createNewNestedContext(detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO, nestedContextName: string, parentContext: DetailViewContext): DetailViewContext {
        const dynamicContext = detailViewContextDTO.nestedDetailViewContexts[nestedContextName];

        if (!dynamicContext)
            return null;

        const nestedContext = this.createNestedDetailViewContext(detailViewContextDTO, parentContext, nestedContextName);
        nestedContext.defaultIterators = dynamicContext.assoziationGridIterators;
        nestedContext.publicUserData = dynamicContext.publicUserData;
        nestedContext.id = detailViewContextDTO.id;
        nestedContext.detailViewContextDTO = dynamicContext;
        nestedContext.genericObject = this.createNestedGenericObject(dynamicContext, nestedContext);

        const uiListener = dynamicContext.detailView.uiListener;
        nestedContext.uiListener = UIListener.createInstance(uiListener);

        return nestedContext;
    }

    private createNestedDetailViewContext(dto: CoreFrameDTOs.IDetailViewContextDTO, parent: DetailViewContext, contextName: string): DetailViewContext {
        const objectInformation = dto.nestedDetailViewContexts[contextName];

        return new DetailViewContext({
            autoInit: false,
            viewModel: this,
            parent: parent,
            pdoId: objectInformation.subject.pdoId,
            action: parseInt(objectInformation.subject.pdoId) > 0 ? 'EDIT' : 'CREATE',
            detailViewContextDTO: objectInformation,
            nestedContextName: contextName,
            pdTypeName: objectInformation.subject.pdTypeName,
            isForcedToUpdate: false
        });
    }

    private createNestedGenericObject(detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO, nestedContext: DetailViewContext): GenericObject {
        let genericObject = new GenericObject(detailViewContextDTO.subject, undefined, this.handleModifiedNestedContext(detailViewContextDTO));

        genericObject.onChange((property) => {
            nestedContext.uiListener['onPropertyValueUpdatedInternal'](nestedContext, property);
            nestedContext.uiListener.onGenericObjectUpdated(nestedContext, genericObject.changes());
        }, null, nestedContext);

        if (detailViewContextDTO.isNewObject) {
            genericObject.isModified(true);
        }

        genericObject.onDelayedChange(() => {
            const dv = nestedContext.detailView();
            if (dv.autoUpdateDerivedProperties !== false) {
                nestedContext.updateProperties();
            }
        }, this);

        genericObject.refresh(detailViewContextDTO.subject)
        return genericObject;
    }

    private reloadNestedContext(currentContext: DetailViewContext, nestedContext: DetailViewContext, detailViewContextDTO: IDetailViewContextDTO) {
        const allContext = currentContext._nestedDetailViewContexts();
        allContext[this.nestedContextName] = nestedContext;
        currentContext._nestedDetailViewContexts(allContext);

        const nestedContextItem = currentContext.getCreatedFactories()
            .filter(x => x.identifier.includes('NestedDetailViewContextFactory'))[0] as NestedDetailViewContextFactory;

        if (!nestedContextItem) {
            console.error("No NestedDetailViewContextFactory could be found.")
            return;
        }

        const detailView = detailViewContextDTO.nestedDetailViewContexts[this.nestedContextName].detailView;
        nestedContext.detailView(detailView)
        nestedContextItem.nestedContext().detailView(detailView);
        nestedContextItem.nestedContext(nestedContext);
    }

    private handleModifiedNestedContext(detailViewContextDTO: CoreFrameDTOs.IDetailViewContextDTO): KnockoutObservable<boolean> {
        const trigger = this.observableOrDefault(false).extend({notify: 'always'});
        return this.defineComputed({
            read: () => {
                trigger();

                return detailViewContextDTO.subject.isModified;
            },
            write: (value: boolean) => {
                detailViewContextDTO.subject.isModified = value;
                trigger(true);
            }
        });
    }
}

ComponentManager.setFactory('NestedDetailViewContext', NestedDetailViewContextFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');